/**
 * ShareWithClientModal
 *
 * Admin-facing modal that surfaces a shareable client-portal URL for a
 * specific workspace. Two portal surfaces are supported:
 *
 *  - "Portale cliente"  -> default client portal, lands on whatever
 *                          `redirectPath` the caller passes (a per-
 *                          surface deep link such as `/client/<id>/posts`,
 *                          `/client/<id>/ideas`, `/client/<id>/review/media`,
 *                          `/client/<id>/reel`). Falls back to the portal
 *                          dashboard when the caller doesn't pass one.
 *  - "Portale creator"  -> creator portal, scoped to this client via
 *                          `/creator/<clientId>`.
 *
 * The URL is a static `/portal/login?redirect=…` link — no token is
 * minted here. The recipient enters their email on `/portal/login`,
 * receives a magic-link email, and the redirect is honoured at the
 * exchange step. Admin impersonation (one-shot magic links) lives on a
 * separate route and is unchanged.
 *
 * All user-visible copy is Italian.
 */

import React, { useEffect, useMemo, useState } from 'react';
import { Modal } from '@/components/ui/Modal';
import { Button } from '@/components/ui/Button';
import {
  CheckIcon,
  CopyIcon,
} from '@/components/icons';
import { copyToClipboard } from '@/utils/clipboard';
import { buildClientPortalUrl } from '@/utils/clientPortalUrl';

interface ShareWithClientModalProps {
  isOpen: boolean;
  onClose: () => void;
  clientId: string;
  clientName: string;
  /**
   * Path the recipient should land on after authenticating, e.g.
   * `/client/<id>/posts` or `/client/<id>/ideas`. Applies only to the
   * "client" tab. Omit to land on the portal dashboard.
   */
  redirectPath?: string;
}

/**
 * Which portal the shared URL should land in.
 * - `client`  -> uses `redirectPath` if provided.
 * - `creator` -> redirect into `/creator/<clientId>`.
 */
type PortalTarget = 'client' | 'creator';

interface TabDefinition {
  id: PortalTarget;
  label: string;
  description: string;
}

const TABS: readonly TabDefinition[] = [
  {
    id: 'client',
    label: 'Portale cliente',
    description: 'Apre la dashboard standard del portale cliente.',
  },
  {
    id: 'creator',
    label: 'Portale creator',
    description:
      'Apre il portale creator (foto, video, brief, idee) per questo cliente.',
  },
] as const;

export const ShareWithClientModal: React.FC<ShareWithClientModalProps> = ({
  isOpen,
  onClose,
  clientId,
  clientName,
  redirectPath,
}) => {
  const [target, setTarget] = useState<PortalTarget>('client');
  const [copied, setCopied] = useState(false);

  const portalUrl = useMemo(() => {
    const path =
      target === 'creator' ? `/creator/${clientId}` : redirectPath;
    return buildClientPortalUrl({ redirectPath: path });
  }, [target, clientId, redirectPath]);

  // Reset transient state whenever the modal is reopened.
  useEffect(() => {
    if (!isOpen) return;
    setTarget('client');
    setCopied(false);
  }, [isOpen]);

  const handleClose = () => {
    setCopied(false);
    setTarget('client');
    onClose();
  };

  const handleCopy = async () => {
    await copyToClipboard(portalUrl);
    setCopied(true);
    window.setTimeout(() => setCopied(false), 2000);
  };

  const activeTab = TABS.find((tab) => tab.id === target) ?? TABS[0];

  const footerContent = (
    <Button onClick={handleClose} variant="ghost">
      Chiudi
    </Button>
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={`Condividi con ${clientName}`}
      size="md"
      footer={footerContent}
    >
      <div className="p-6">
        <div className="space-y-6">
          {/* Portal target tabs */}
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">
              Scegli il portale
            </label>
            <div
              role="tablist"
              aria-label="Portale di destinazione"
              className="flex gap-1 bg-gray-100 rounded-md p-1"
            >
              {TABS.map((tab) => {
                const isActive = tab.id === target;
                return (
                  <button
                    key={tab.id}
                    type="button"
                    role="tab"
                    aria-selected={isActive}
                    onClick={() => setTarget(tab.id)}
                    className={`flex-1 px-3 py-1.5 text-xs font-medium rounded transition-colors ${
                      isActive
                        ? 'bg-white text-gray-900 shadow-sm'
                        : 'text-gray-600 hover:text-gray-900'
                    }`}
                  >
                    {tab.label}
                  </button>
                );
              })}
            </div>
            <p className="text-xs text-gray-500">{activeTab.description}</p>
          </div>

          {/* Success banner */}
          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center shrink-0">
                <CheckIcon className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <h3 className="font-semibold text-green-900 text-sm">
                  Link al portale
                </h3>
                <p className="text-green-700 text-sm mt-1">
                  Condividi questo link con{' '}
                  <span className="font-medium">{clientName}</span>. Il
                  destinatario inserirà la propria email per ricevere un
                  link di accesso.
                </p>
              </div>
            </div>
          </div>

          {/* Portal URL field */}
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">
              Link da condividere
            </label>
            <div className="flex items-center gap-2">
              <input
                type="text"
                value={portalUrl}
                readOnly
                className="flex-1 p-2 bg-gray-50 border border-gray-200 rounded-sm text-sm font-mono text-gray-600 truncate"
              />
              <button
                type="button"
                onClick={handleCopy}
                className={`inline-flex items-center gap-1.5 px-3 py-2 text-xs font-medium rounded transition-colors whitespace-nowrap ${
                  copied
                    ? 'bg-green-100 text-green-700 border border-green-200'
                    : 'bg-white text-gray-600 border border-gray-200 hover:bg-gray-50'
                }`}
              >
                <CopyIcon className="w-3.5 h-3.5" />
                {copied ? 'Copiato' : 'Copia'}
              </button>
            </div>
            <p className="text-xs text-gray-500">
              Link riutilizzabile. L'autenticazione avviene via email del
              destinatario.
            </p>
          </div>
        </div>
      </div>
    </Modal>
  );
};
